import React from 'react';
import { User, Bot, AlertCircle } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { Message, MessageMetadata } from '../../types/chat';
import { MessageContent } from './MessageContent';
import { MessageActions } from './MessageActions';
import { UsageStats } from './UsageStats';
import { WorkflowInfo } from './WorkflowInfo';
import { AnimatedAvatar } from '../agents/AnimatedAvatar';
import { useAgent } from '../../contexts/AgentContext';


interface MessageBubbleProps {
  message: Message;
  isLiveResponse: boolean;
  sending?: boolean;
  onRegenerate?: (index: number) => void;
  index?: number;
  isSharedConversation?: string;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ 
  message, 
  isLiveResponse,
  sending,
  onRegenerate,
  index,
  isSharedConversation
}) => {
  const { showToast } = useToast();
  const { mainAgent } = useAgent();
  if (!message) return null;

  


  return (
    <div className={`flex items-start sm:space-x-3 ${
      message.sender === 'user' ? 'sm:flex-row-reverse sm:space-x-reverse' : ''
    }`}>
      <div className="hidden sm:block flex-shrink-0">
        {message.sender === 'user' ? (
          message.avatar ? (
            <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-600 dark:bg-gray-500">
              <img 
                src={message.avatar} 
                alt="User Avatar" 
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-600 dark:bg-gray-500">
              <User size={20} className="text-white" />
            </div>
          )
        ) : (
          <AnimatedAvatar 
            src={mainAgent.avatar}
            alt={`${mainAgent.name} Avatar`}
            size="md"
            // Only animate when this is a live response (message is being streamed)
            isAnimating={isLiveResponse && sending}
            // Disable hover effect in chat messages
            disableHoverEffect={true}
            enabled={mainAgent.enabled}
          />
        )}
      </div>
      <div className={`flex flex-col space-y-1 min-w-0 flex-1 ${
        message.sender === 'user' ? 'items-end' : ''
      }`}>
        <div 
          className={`rounded-lg px-4 py-2 max-w-full sm:max-w-[calc(100%-2rem)] break-words ${
            message.sender === 'user'
              ? message.error
                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                : 'bg-blue-100 dark:bg-blue-700 text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-500/10 hover:via-purple-500/10 hover:to-pink-500/10 dark:hover:from-blue-500/20 dark:hover:via-purple-500/20 dark:hover:to-pink-500/20 text-gray-700 dark:text-gray-300 cursor-pointer'
              : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
          }`}
          style={{ overflowWrap: "break-word", wordBreak: "break-word" }}
          onClick={async () => {
            if (message.sender === 'user' && message.content) {
              await navigator.clipboard.writeText(message.content);
              showToast({
                message: 'User message copied to clipboard',
                type: 'success',
                duration: 2000
              });
            }
          }}>
          {message.sender === 'assistant' && sending && !message.content ? (
            <div className="flex space-x-1">
              <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full typing-dot" />
              <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full typing-dot" />
              <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full typing-dot" />
            </div>
          ) : (
            <MessageContent 
              content={message.content || ''} 
              isLiveResponse={isLiveResponse}
            />
          )}
          {message.error && (
            <div className="flex items-center space-x-1 mt-1 text-sm text-red-600 dark:text-red-400">
              <AlertCircle size={14} />
              <span>Failed to send message</span>
            </div>
          )}
          {message.sender === 'assistant' && (
            <>
              {/* Display workflow info if available */}
              {message.metadata?.workflow_info?.steps && (
                <WorkflowInfo 
                  steps={message.metadata.workflow_info.steps.filter((step): step is { title: string; timestamp: string } => 
                    step.title !== null && step.timestamp !== null
                  )} 
                  isLive={isLiveResponse}
                />
              )}
              {/* Display usage stats if available */}
              {message.metadata?.usage && (
                <UsageStats 
                  usage={message.metadata.usage} 
                  isPartial={sending}
                />
              )}
              {!sending && <MessageActions message={message} onRegenerate={onRegenerate && index !== undefined ? () => onRegenerate(index) : undefined} isSharedConversation={isSharedConversation} />}
            </>
          )}
        </div>
        {message.created_at && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(message.created_at).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
};
